import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  LabelList,
  BarProps,
} from 'recharts';
import { useEffect, useMemo, useState } from 'react';
import { useQuestionStatistics } from './useQuestionStatistic';
import { Question } from '../../../../../store/subCategory';
import { IQuestionStatistic } from '../../../../../hooks/api/questionStatistic';
import { filterStatisticByUserType } from '../helpers';
import { QuestionUserType } from '../../../../../hooks/api/questions';

import styles from './index.module.scss';

const COLORS = ['#2F80ED', '#7AAEF4', '#ACCCF8'];

interface QuestionStatisticProps {
  questions: Question[];
  subCategoryId: string;
  userType: QuestionUserType;
}

/** I'm just minimally refactoring code, it was written way worse by prev author :) */
function BarShape(props: BarProps['shape'] | any) {
  const { x, y, width, height, fill } = props;

  return <rect x={x} y={y} width={width - 1} height={height} fill={fill} />;
}

function QuestionStatistic({
  subCategoryId,
  questions,
  userType = 'all',
}: QuestionStatisticProps) {
  const { getQuestionStatistic, response } = useQuestionStatistics();
  const [allQuestStats, setAllQuestStats] = useState<
    IQuestionStatistic['list']
  >([]);
  const [consumerQuestStats, setConsumerQuestStats] = useState<
    IQuestionStatistic['list']
  >([]);
  const [workerQuestStats, setWorkerQuestStats] = useState<
    IQuestionStatistic['list']
  >([]);

  const userTypeLabel = useMemo(
    () => (userType === 'worker' ? 'Worker' : 'Customer'),
    [userType]
  );

  useEffect(() => {
    if (response && response?.data?.list?.length) {
      setAllQuestStats(filterStatisticByUserType(response.data.list, 'all'));
      setConsumerQuestStats(
        filterStatisticByUserType(response.data.list, 'customer')
      );
      setWorkerQuestStats(
        filterStatisticByUserType(response.data.list, 'worker')
      );
    }
  }, [response]);

  useEffect(() => {
    if (subCategoryId) {
      getQuestionStatistic(subCategoryId);
    }
  }, [subCategoryId]);

  const charts = useMemo(() => {
    /** if no stats in all categories - return [] */
    if (
      [...workerQuestStats, ...consumerQuestStats, ...allQuestStats].length ===
      0
    )
      return [];

    const prepareData = (dataParam: IQuestionStatistic['list']) =>
      (dataParam || [])
        .filter(
          (q) =>
            true /* TODO (BE): filter subCategory questions by questions.some(question => question.id === q.id) */
        )
        .flatMap((block) => {
          const { legend, data } = block;

          return data.map((questionData) => {
            const values = questionData.numbers.filter(
              (item) => item.value > 0
            );

            const row: { name: string; [key: string]: number | string } = {
              name: questionData.question,
            };

            values.forEach((item) => {
              row[item.valueofChoice] = item.value;
            });

            return {
              question: questionData.question,
              chartData: [row],
              fullLegend: questionData.numbers.map((item) => ({
                value: item.valueofChoice,
                label:
                  legend.find((l) => l.value === item.valueofChoice)?.label ||
                  '',
              })),
              nonZeroKeys: values.map((item) => item.valueofChoice),
            };
          });
        });

    if (userType === 'worker') {
      return prepareData(workerQuestStats);
    }

    return prepareData([...consumerQuestStats, ...allQuestStats]);
  }, [allQuestStats, consumerQuestStats, workerQuestStats]);

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>{userTypeLabel} Statistic</h3>
      <div className={styles.subTitle}>We surveyed 100000 respondents</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {charts.map(
          ({ question, chartData, fullLegend, nonZeroKeys }, chartIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={chartIndex} className={styles.chartWrapper}>
              <div className={styles.contentRow}>
                <div className={styles.questionBlock}>
                  <h4 className={styles.questionText}>
                    {chartIndex + 1}. {question}
                  </h4>
                </div>

                <div className={styles.innerWrapper}>
                  <div className={styles.chartBlock}>
                    <ResponsiveContainer width="100%" height={32}>
                      <BarChart data={chartData} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} hide />
                        <YAxis type="category" dataKey="name" hide />
                        {fullLegend.map((legendItem, index) => {
                          const key = legendItem.value;
                          const isVisible = nonZeroKeys.includes(key);

                          if (!isVisible) return null;

                          return (
                            <Bar
                              key={key}
                              dataKey={key}
                              stackId="a"
                              isAnimationActive={false}
                              shape={(props: any) => BarShape(props)}
                            >
                              <Cell fill={COLORS[index % COLORS.length]} />
                              <LabelList
                                dataKey={key}
                                position="center"
                                formatter={(value: number) =>
                                  value > 0 ? `${value.toFixed(0)}%` : ''
                                }
                                style={{
                                  fill: 'white',
                                  fontSize: '11px !important',
                                  fontWeight: 500,
                                  textAnchor: 'middle',
                                }}
                              />
                            </Bar>
                          );
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className={styles.legendRow}>
                    {fullLegend.map((item, index) => (
                      <div key={item.value} className={styles.legendItem}>
                        <div
                          className={styles.legendColor}
                          style={{
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                        <span className={styles.legendLabel}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default QuestionStatistic;
